import { useEffect, useMemo, useState } from "react";
import {
  fetchConfigurableConcepts,
  normalizePlanTypeApiCode,
  type ConfigurableConcept,
} from "../services/autoTaggingService";

export interface UseConfigurableConceptsResult {
  concepts: ConfigurableConcept[];
  conceptOptions: ConfigurableConcept[];
  loading: boolean;
  error: string | null;
}

function normalizeTargetTypes(
  targetTypeOrTypes?: string | null | readonly string[],
): string[] {
  if (Array.isArray(targetTypeOrTypes)) {
    const seen = new Set<string>();
    const types: string[] = [];
    for (const item of targetTypeOrTypes) {
      const code = normalizePlanTypeApiCode(item);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      types.push(code);
    }
    return types;
  }
  const single = normalizePlanTypeApiCode(targetTypeOrTypes ?? "");
  return single ? [single] : [];
}

function mergeConcepts(
  groups: readonly ConfigurableConcept[][],
): ConfigurableConcept[] {
  const seen = new Set<string>();
  const merged: ConfigurableConcept[] = [];
  for (const items of groups) {
    for (const item of items) {
      const code = item.code.trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      merged.push(item);
    }
  }
  return merged;
}

/**
 * Loads configurable concepts for one or more API `target_type` values.
 * Pass an array (e.g. all object types) to merge options for filter "All".
 */
export function useConfigurableConcepts(
  targetTypeOrTypes?: string | null | readonly string[],
  subType?: string | null,
): UseConfigurableConceptsResult {
  const [concepts, setConcepts] = useState<ConfigurableConcept[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const targetTypes = useMemo(
    () => normalizeTargetTypes(targetTypeOrTypes),
    [targetTypeOrTypes],
  );
  const targetTypesKey = targetTypes.join("|");
  const subTypeCode = normalizePlanTypeApiCode(subType ?? "");

  useEffect(() => {
    let mounted = true;

    if (targetTypes.length === 0) {
      setConcepts([]);
      setError(null);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);

    Promise.all(
      targetTypes.map((type) =>
        fetchConfigurableConcepts(type, subTypeCode || undefined),
      ),
    )
      .then((groups) => {
        if (!mounted) return;
        setConcepts(mergeConcepts(groups));
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setConcepts([]);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load configurable concepts",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
    // targetTypesKey is a stable serialization of targetTypes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefer key over array identity
  }, [subTypeCode, targetTypesKey]);

  const conceptOptions = useMemo(() => concepts, [concepts]);

  return {
    concepts,
    conceptOptions,
    loading,
    error,
  };
}
